import dayjs, { Dayjs } from 'dayjs';

const businessDayFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Warsaw', year: 'numeric', month: '2-digit', day: '2-digit',
});

/** Polish business day of an instant; mirror of backend businessDateOf. */
export const businessDateOf = (value: string | Date | null | undefined): string | null => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return businessDayFormatter.format(date);
};

/**
 * Safely formats a date string to YYYY-MM-DD format
 * Returns '-' if the date is invalid, null, or undefined
 */
export const formatDate = (date: string | Date | null | undefined): string => {
  if (!date) {
    return '-';
  }
  
  const dayjsDate = dayjs(date);
  if (!dayjsDate.isValid()) {
    return '-';
  }
  
  return dayjsDate.format('YYYY-MM-DD');
};

/**
 * Safely formats a date string to YYYY-MM-DD HH:mm format
 * Returns '-' if the date is invalid, null, or undefined
 */
export const formatDateTime = (date: string | Date | null | undefined): string => {
  if (!date) {
    return '-';
  }
  
  const dayjsDate = dayjs(date);
  if (!dayjsDate.isValid()) {
    return '-';
  }
  
  return dayjsDate.format('YYYY-MM-DD HH:mm');
};

/**
 * Safely formats a date string to DD.MM.YYYY format (European style, date only)
 * Returns '-' if the date is invalid, null, or undefined
 *
 * Date-only sibling of formatDateTimeEuropean, for surfaces that mirror a
 * Polish paper/spreadsheet document (e.g. the batch ledger) where the ISO
 * YYYY-MM-DD of formatDate would read as a technical value.
 */
export const formatDateEuropean = (date: string | Date | null | undefined): string => {
  if (!date) {
    return '-';
  }

  const dayjsDate = dayjs(date);
  if (!dayjsDate.isValid()) {
    return '-';
  }

  return dayjsDate.format('DD.MM.YYYY');
};

/**
 * Safely formats a date string to DD.MM.YYYY HH:mm format (European style)
 * Returns '-' if the date is invalid, null, or undefined
 */
export const formatDateTimeEuropean = (date: string | Date | null | undefined): string => {
  if (!date) {
    return '-';
  }
  
  const dayjsDate = dayjs(date);
  if (!dayjsDate.isValid()) {
    return '-';
  }
  
  return dayjsDate.format('DD.MM.YYYY HH:mm');
};

/**
 * Safely converts a date string to a dayjs object for DatePicker components
 * Returns null if the date is invalid, null, or undefined
 */
export const parseDate = (date: string | Date | null | undefined): Dayjs | null => {
  if (!date) {
    return null;
  }
  
  const dayjsDate = dayjs(date);
  if (!dayjsDate.isValid()) {
    return null;
  }
  
  return dayjsDate;
};

/**
 * Checks if a date value is valid
 */
export const isValidDate = (date: string | Date | null | undefined): boolean => {
  if (!date) {
    return false;
  }
  
  const dayjsDate = dayjs(date);
  return dayjsDate.isValid();
};

/**
 * Safely converts a Dayjs object to ISO string for API calls
 * Returns null if the date is invalid or null
 */
export const toISOString = (date: Dayjs | null | undefined): string | null => {
  if (!date || !date.isValid()) {
    return null;
  }
  
  return date.toISOString();
};